import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Divider, Form, message, Modal} from 'antd';
import dayjs from 'dayjs';
import {PeriodFields} from "./subsubcomponents/PeriodFields";
import {TotalsHeader} from "./subsubcomponents/TotalsHeader";
import {useStaffData} from "./hooks/useStaffData";
import {useTotalsCalculator} from "./hooks/useTotalsCalculator";
import {BasicInfoFields} from "./subsubcomponents/BasicInfoFields";
import {StaffTable} from "./subsubcomponents/StaffTable";
import {StaffSelection} from "./subsubcomponents/StaffSelection";
import {instance} from "../../../../../API/API";

export const salaryStatementsAPI = {
    // Создание новой ведомости
    createStatement: async (statementData) => {
        try {
            const response = await instance.post('finance_04122025/create_statement.php', statementData);
            return response.data;
        } catch (error) {
            console.error('Ошибка создания ведомости:', error);
            throw error;
        }
    },

    // Получение ведомостей с фильтрами
    getStatements: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            // Добавляем фильтры в параметры запроса
            if (filters.year) params.append('year', filters.year);
            if (filters.month) params.append('month', filters.month);
            if (filters.status) params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            if (filters.page) params.append('page', filters.page);
            if (filters.limit) params.append('limit', filters.limit);

            const response = await instance.get(`finance_04122025/get_statements.php?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Ошибка получения ведомостей:', error);
            throw error;
        }
    },

    // Обновление ведомости
    updateStatement: async (id, statementData) => {
        try {
            const response = await instance.put(`finance_04122025/update_statement.php?id=${id}`, statementData);
            return response.data;
        } catch (error) {
            console.error('Ошибка обновления ведомости:', error);
            throw error;
        }
    },

    // Удаление ведомости
    deleteStatement: async (id) => {
        try {
            const response = await instance.delete(`finance_04122025/delete_statement.php?id=${id}`);
            return response.data;
        } catch (error) {
            console.error('Ошибка удаления ведомости:', error);
            throw error;
        }
    }
};

const CreateStatementModal = ({visible, onCancel, onSubmit, refreshList}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false); // Новое состояние

    // Используем хук для работы с данными сотрудников
    // Для создания ведомости передаем false как isEditMode
    const {
        staffLoading,
        availableStaff,
        selectedStaff,
        staffAmounts,
        handleAddStaff,
        handleRemoveStaff,
        handleAddAllStaff,
        handleAmountChange,
        resetStaffData
    } = useStaffData(visible, false); // false = не режим редактирования

    // Используем хук для расчета итогов
    const totals = useTotalsCalculator(staffAmounts);

    // Генерация списка годов
    const yearOptions = useMemo(() => {
        const currentYear = dayjs().year();
        const years = [];
        for (let year = 2024; year <= currentYear + 1; year++) {
            years.push(year);
        }
        return years;
    }, []);

    // Автоматическое обновление общей суммы в форме - только если форма существует
    useEffect(() => {
        if (form) {
            form.setFieldsValue({
                totalAmount: totals.totalAmount
            });
        }
    }, [totals.totalAmount, form]);

    const handleSubmit = useCallback(async () => {
        // Предотвращаем множественные нажатия
        if (isSubmitting || submitAttempted) {
            console.log('Предотвращено повторное нажатие кнопки');
            return;
        }

        try {
            setSubmitAttempted(true);
            const values = await form.validateFields();

            if (selectedStaff.length === 0) {
                message.error('Добавьте хотя бы одного сотрудника');
                setSubmitAttempted(false);
                return;
            }

            const formattedData = {
                ...values,
                date: `${values.year}-${values.month.toString().padStart(2, '0')}`,
                employeeCount: selectedStaff.length,
                totalAmount: totals.totalAmount,
                cashAmount: totals.totalCash,
                nonCashWithoutVAT: totals.totalNonCashWithoutVAT,
                nonCashWithVAT: totals.totalNonCashWithVAT,
                status: 'active',
                staffIds: selectedStaff.map(staff => staff.id),
                staffDetails: selectedStaff.map(staff => ({
                    id: staff.id,
                    fullName: staff.fullName,
                    storeId: staff.storeId,
                    amounts: staffAmounts[staff.id] || {
                        cash: 0,
                        nonCashWithoutVAT: 0,
                        nonCashWithVAT: 0
                    }
                })),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            console.log('Отправка данных ведомости:', formattedData);
            setIsSubmitting(true);
            setLoading(true);

            try {
                const result = await salaryStatementsAPI.createStatement(formattedData);

                if (result.success) {
                    console.log('Ведомость успешно создана, ID:', result.data?.id);
                    message.success('Ведомость успешно создана');

                    // Вызываем onSubmit с данными
                    if (onSubmit) {
                        onSubmit(formattedData);
                    }

                    // Очистка формы и данных
                    form.resetFields();
                    resetStaffData();

                    // Обновление списка ведомостей, если передан callback
                    if (refreshList) {
                        refreshList();
                    }

                    // Закрытие модального окна
                    onCancel();
                } else {
                    message.error(result.message || 'Ошибка при создании ведомости');
                    setSubmitAttempted(false);
                }
            } catch (error) {
                console.error('Ошибка при создании ведомости:', error);
                message.error('Ошибка при создании ведомости. Проверьте подключение к серверу.');
                setSubmitAttempted(false);
            }
        } catch (error) {
            console.error('Ошибка валидации:', error);
            if (error.errorFields) {
                message.error('Пожалуйста, заполните все обязательные поля правильно');
            }
            setSubmitAttempted(false);
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    }, [form, selectedStaff, staffAmounts, totals, onSubmit, refreshList, onCancel, resetStaffData, isSubmitting, submitAttempted]);

    const handleCancel = useCallback(() => {
        form.resetFields();
        resetStaffData();
        setSubmitAttempted(false);
        onCancel();
    }, [form, onCancel, resetStaffData]);

    // Сброс состояния при открытии/закрытии модального окна
    useEffect(() => {
        if (visible) {
            // При открытии сбрасываем форму и состояние
            form.resetFields();
            resetStaffData();
            setSubmitAttempted(false);
        }
    }, [visible, form, resetStaffData]);

    // Дополнительная защита от двойного закрытия
    const handleModalCancel = useCallback(() => {
        if (isSubmitting) {
            message.warning('Пожалуйста, дождитесь завершения создания ведомости');
            return;
        }
        handleCancel();
    }, [isSubmitting, handleCancel]);

    return (
        <Modal
            title="Создание новой ведомости"
            open={visible}
            onOk={handleSubmit}
            onCancel={handleModalCancel}
            confirmLoading={loading}
            width={1100}
            okText="Создать"
            cancelText="Отмена"
            okButtonProps={{
                disabled: isSubmitting || selectedStaff.length === 0 || submitAttempted
            }}
            destroyOnClose={true}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    totalAmount: 0,
                    year: dayjs().year(),
                    month: dayjs().month() + 1,
                }}
                validateTrigger="onBlur"
            >
                <BasicInfoFields/>
                <PeriodFields form={form} yearOptions={yearOptions}/>

                <Divider style={{margin: '16px 0'}}>Сотрудники в ведомости</Divider>

                <TotalsHeader
                    selectedStaff={selectedStaff}
                    totals={totals}
                />

                {/* Таблица сотрудников */}
                <StaffTable
                    selectedStaff={selectedStaff}
                    staffAmounts={staffAmounts}
                    handleAmountChange={handleAmountChange}
                    handleRemoveStaff={handleRemoveStaff}
                    totals={totals}
                />

                {/* Выбор дополнительных сотрудников (только если есть доступные) */}
                {availableStaff.length > 0 && (
                    <>
                        <Divider style={{margin: '16px 0'}}>Добавить сотрудников</Divider>
                        <StaffSelection
                            staffLoading={staffLoading}
                            availableStaff={availableStaff}
                            handleAddStaff={handleAddStaff}
                            handleAddAllStaff={handleAddAllStaff}
                            disabled={staffLoading}
                        />
                    </>
                )}

                {/* Информационное сообщение, если все сотрудники уже добавлены */}
                {availableStaff.length === 0 && selectedStaff.length > 0 && (
                    <div style={{marginTop: 16, textAlign: 'center'}}>
                        <p>Все доступные сотрудники уже добавлены в ведомость</p>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default CreateStatementModal;